import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Select from 'react-select/creatable';
import { useVideo } from '../../context/VideoContext';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import { slugify } from '../../common/utils';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiX, FiEdit2, FiLink, FiFilm, FiZap, FiLoader, FiDollarSign, FiTrendingUp, FiEye } = FiIcons;

const DropdownEditor = ({ title, items, onSave, onClose }) => {
  const [editText, setEditText] = useState('');

  useEffect(() => {
    setEditText(items.join('\n'));
  }, [items]);

  const handleSave = () => {
    const newItems = editText
      .split('\n')
      .map(item => item.trim())
      .filter(item => item.length > 0);
    onSave(newItems);
    onClose();
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="glass-card p-6 w-full max-w-md"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-white/80 text-sm font-medium mb-2">
              Options (one per line)
            </label>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all resize-none"
              rows="8"
              placeholder="Enter options, one per line..."
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} className="btn-primary flex items-center gap-2">
              <SafeIcon icon={FiSave} /> Save Changes
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const VideoForm = ({ video, onClose }) => {
  const { addVideo, updateVideo, industries, visualStyles, videoTypes, tags, saveIndustries, saveVisualStyles, saveVideoTypes, videos } = useVideo();
  const { role } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_link: '',
    industry: '',
    visual_style: [],
    client: '',
    estimated_cost: '5000',
    market_score: '0',
    video_type: [],
    duration: '00:00',
    published_on: new Date().toISOString().split('T')[0],
    tags: [],
    views: '0',
    audience: '',
    why_it_works: '',
  });

  const [isAutofilling, setIsAutofilling] = useState(false);
  const [editors, setEditors] = useState({ ind: false, sty: false, typ: false });

  useEffect(() => {
    if (video) {
      setFormData({
        title: video.title || '',
        description: video.description || '',
        video_link: video.video_link || '',
        industry: video.industry || '',
        visual_style: Array.isArray(video.visual_style) ? video.visual_style : (video.visual_style ? [video.visual_style] : []),
        client: video.client || '',
        estimated_cost: video.estimated_cost || '5000',
        market_score: video.market_score || '0',
        video_type: Array.isArray(video.video_type) ? video.video_type : (video.video_type ? [video.video_type] : []),
        duration: video.duration || '00:00',
        published_on: video.published_on || new Date().toISOString().split('T')[0],
        tags: video.tags || [],
        views: video.views || '0',
        audience: video.audience || '',
        why_it_works: video.why_it_works || '',
      });
    }
  }, [video]);

  const detectVideoInfo = (url) => {
    const youtubeRegex = new RegExp('(?:youtube\\.com/(?:[^/]+/.+/|(?:v|e(?:mbed)?)/|.*[?&]v=)|youtu\\.be/)([^"&?/\\s]{11})', 'i');
    const vimeoRegex = new RegExp('(?:vimeo\\.com/|player\\.vimeo\\.com/video/)([0-9]+)', 'i');
    const ytMatch = url.match(youtubeRegex);
    if (ytMatch) return { platform: 'youtube', id: ytMatch[1] };
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) return { platform: 'vimeo', id: vimeoMatch[1] };
    return null;
  };

  const handleAutofill = async () => {
    const info = detectVideoInfo(formData.video_link);
    if (!info) {
      alert("Please enter a valid YouTube or Vimeo URL first.");
      return;
    }
    setIsAutofilling(true);
    try {
      const response = await fetch('https://hook.us1.make.com/s45ib8w6g6uf3aw3bo7ud6xa2oshla4p', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(info)
      });
      if (response.ok) {
        const data = await response.json();
        const mappedTags = data.Tags ? data.Tags.split(',').map(t => t.trim()) : [];
        const mappedStyles = data["Visual Style"] ? data["Visual Style"].split(',').map(s => s.trim()) : [];
        const mappedTypes = data["Video Type"] ? (Array.isArray(data["Video Type"]) ? data["Video Type"] : data["Video Type"].split(',').map(t => t.trim())) : [];
        const pubDate = data["Published On"] ? new Date(data["Published On"]).toISOString().split('T')[0] : formData.published_on;

        setFormData(prev => ({
          ...prev,
          title: data.Title || prev.title,
          description: data.Description || prev.description,
          video_type: mappedTypes.length > 0 ? [mappedTypes[0]] : prev.video_type,
          duration: data.Duration || prev.duration,
          published_on: pubDate,
          industry: data.Industry || prev.industry,
          visual_style: mappedStyles,
          tags: mappedTags,
          audience: data.audience || prev.audience,
          why_it_works: data.why_it_works || prev.why_it_works,
        }));
      }
    } catch (error) {
      console.error("Autofill error:", error);
      alert("Failed to fetch video details.");
    } finally {
      setIsAutofilling(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isDuplicate = videos.some(v =>
      v.title.toLowerCase() === formData.title.toLowerCase() &&
      v.id !== (video ? video.id : null)
    );

    if (isDuplicate) {
      alert("A video with this exact title already exists. Please use a unique title.");
      return;
    }

    let slug = video ? video.slug : slugify(formData.title);
    if (!slug) slug = crypto.randomUUID();

    if (!video) {
      let uniqueSlug = slug;
      let counter = 1;
      while (videos.some(v => v.slug === uniqueSlug)) {
        uniqueSlug = `${slug}-${counter}`;
        counter++;
      }
      slug = uniqueSlug;
    }

    const data = {
      ...formData,
      slug,
      estimated_cost: parseFloat(formData.estimated_cost) || 500,
      market_score: parseFloat(formData.market_score) || 0,
      views: parseInt(formData.views) || 0,
      visual_style: formData.visual_style,
      video_type: formData.video_type,
      audience: formData.audience || null,
      why_it_works: formData.why_it_works || null,
    };

    if (video) await updateVideo(video.id, data);
    else await addVideo(data);
    onClose();
  };

  const inputClasses = "w-full px-4 py-3 bg-zinc-900/50 border border-white/10 rounded-2xl text-white placeholder-white/20 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none";
  const selectClasses = `${inputClasses} appearance-none custom-select cursor-pointer`;

  const reactSelectStyles = {
    control: ({ isFocused }) => `!bg-zinc-900/50 !border ${isFocused ? '!border-orange-500 !ring-1 !ring-orange-500/20' : '!border-white/10'} !rounded-2xl !min-h-[48px] !px-2 !transition-all !cursor-pointer !shadow-none`,
    placeholder: () => "!text-white/20 !text-sm",
    menu: () => "!bg-[#0f0f12] !border !border-white/10 !rounded-2xl !mt-2 !overflow-hidden !shadow-2xl !z-[100]",
    option: ({ isFocused, isSelected }) => `!px-4 !py-2.5 !text-sm !cursor-pointer !transition-colors ${isSelected ? '!bg-orange-500/20 !text-orange-400' : isFocused ? '!bg-white/5 !text-orange-400' : '!text-slate-300'}`,
    multiValue: () => "!bg-orange-500/10 !border !border-orange-500/30 !rounded-lg !m-1",
    multiValueLabel: () => "!text-orange-200 !px-2 !text-xs !font-bold !py-0.5",
    multiValueRemove: () => "!text-orange-400 hover:!bg-orange-500/20 hover:!text-orange-200 !transition-all",
    singleValue: () => "!text-white",
    input: () => "!text-white",
    dropdownIndicator: () => "!text-white/40 hover:!text-orange-500",
    indicatorSeparator: () => "!bg-white/10",
    valueContainer: () => "!p-1"
  };

  // Single-select video type value
  const videoTypeValue = formData.video_type.length > 0
    ? { value: formData.video_type[0], label: formData.video_type[0] }
    : null;

  return (
    <motion.div
      className="glass-card p-8 border-white/5"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="flex justify-between items-center mb-8 pb-4 border-b border-white/5">
        <h3 className="text-2xl font-bold text-white flex items-center gap-3">
          <SafeIcon icon={FiFilm} className="text-orange-500" />
          {video ? 'Edit Curation' : 'New Curation'}
        </h3>
        <button onClick={onClose} className="text-white/40 hover:text-white"><SafeIcon icon={FiX} className="text-2xl" /></button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Video URL — always full width, above the grid */}
        <div className="space-y-2">
          <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Video URL</label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <SafeIcon icon={FiLink} className="absolute left-4 top-1/2 -translate-y-1/2 text-orange-500/50" />
              <input
                type="url"
                value={formData.video_link}
                onChange={e => setFormData({...formData, video_link: e.target.value})}
                className="w-full pl-12 pr-4 py-4 bg-zinc-900/80 border border-white/10 rounded-2xl text-white focus:border-orange-500 transition-all outline-none"
                placeholder="Paste YouTube or Vimeo link..."
                required
              />
            </div>
            <button
              type="button"
              onClick={handleAutofill}
              disabled={isAutofilling || !formData.video_link}
              className={`px-6 rounded-2xl flex items-center gap-2 font-bold transition-all ${isAutofilling ? 'bg-zinc-800 text-zinc-500' : 'bg-orange-500/10 text-orange-500 border border-orange-500/20 hover:bg-orange-500 hover:text-white'}`}
            >
              {isAutofilling ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiZap} />}
              Autofill
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Project Title — full width */}
          <div className="md:col-span-2 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Project Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              className={inputClasses}
              required
            />
          </div>

          {/* Description — full width */}
          <div className="md:col-span-2 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              className={`${inputClasses} h-24 resize-none`}
            />
          </div>

          {/* Company — half width */}
          <div className="col-span-1 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Company</label>
            <input
              type="text"
              value={formData.client}
              onChange={e => setFormData({...formData, client: e.target.value})}
              className={inputClasses}
            />
          </div>

          {/* Estimated Cost — half width */}
          <div className="col-span-1 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Estimated Cost</label>
            <div className="relative">
              <SafeIcon icon={FiDollarSign} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="number"
                min="500"
                step="500"
                value={formData.estimated_cost}
                onChange={e => setFormData({...formData, estimated_cost: e.target.value})}
                className="w-full pl-10 pr-4 py-3 bg-zinc-900/50 border border-white/10 rounded-2xl text-white focus:border-orange-500 transition-all outline-none"
              />
            </div>
          </div>

          {/* Duration — half width */}
          <div className="col-span-1 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Duration</label>
            <input
              type="text"
              value={formData.duration}
              onChange={e => setFormData({...formData, duration: e.target.value})}
              className={inputClasses}
              placeholder="00:00"
            />
          </div>

          {/* Published On — half width */}
          <div className="col-span-1 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Published On</label>
            <input
              type="date"
              value={formData.published_on}
              onChange={e => setFormData({...formData, published_on: e.target.value})}
              className={inputClasses}
            />
          </div>

          {/* Industry — half width */}
          <div className="col-span-1 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Industry</label>
            <div className="flex gap-2">
              <select
                value={formData.industry}
                onChange={e => setFormData({...formData, industry: e.target.value})}
                className={selectClasses}
                required
              >
                <option value="">Select Industry</option>
                {industries.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
              {role === 'admin' && <button type="button" onClick={() => setEditors({...editors, ind: true})} className="btn-secondary px-3 flex-shrink-0"><SafeIcon icon={FiEdit2} /></button>}
            </div>
          </div>

          {/* Visual Styles — half width */}
          <div className="col-span-1 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Visual Styles</label>
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">
                <Select
                  isMulti
                  unstyled
                  classNames={reactSelectStyles}
                  options={visualStyles.map(s => ({ value: s, label: s }))}
                  value={formData.visual_style.map(s => ({ value: s, label: s }))}
                  onChange={selected => setFormData({...formData, visual_style: selected ? selected.map(s => s.value) : []})}
                  placeholder="Select styles..."
                  isCreatable
                />
              </div>
              {role === 'admin' && <button type="button" onClick={() => setEditors({...editors, sty: true})} className="btn-secondary px-3 max-h-[48px] flex-shrink-0"><SafeIcon icon={FiEdit2} /></button>}
            </div>
          </div>

          {/* Video Type — single select, half width */}
          <div className="col-span-1 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Video Type</label>
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">
                <Select
                  isMulti={false}
                  unstyled
                  classNames={reactSelectStyles}
                  options={videoTypes.map(t => ({ value: t, label: t }))}
                  value={videoTypeValue}
                  onChange={selected => setFormData({...formData, video_type: selected ? [selected.value] : []})}
                  placeholder="Select type..."
                  isCreatable
                />
              </div>
              {role === 'admin' && <button type="button" onClick={() => setEditors({...editors, typ: true})} className="btn-secondary px-3 flex-shrink-0 max-h-[48px]"><SafeIcon icon={FiEdit2} /></button>}
            </div>
          </div>

          {/* Audience — half width */}
          <div className="col-span-1 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Audience</label>
            <select
              value={formData.audience}
              onChange={e => setFormData({...formData, audience: e.target.value})}
              className={selectClasses}
            >
              <option value="">Select Audience</option>
              <option value="Developer Tool">Developer Tool</option>
              <option value="Enterprise CXO">Enterprise CXO</option>
              <option value="SMB">SMB</option>
            </select>
          </div>

          {/* Tags — full width */}
          <div className="md:col-span-2 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Tags</label>
            <Select
              isMulti
              unstyled
              classNames={reactSelectStyles}
              options={tags.map(t => ({ value: t.name, label: t.name }))}
              value={formData.tags.map(t => ({ value: t, label: t }))}
              onChange={selected => setFormData({...formData, tags: selected ? selected.map(s => s.value) : []})}
              placeholder="Add tags..."
              isCreatable
            />
          </div>

          {/* Why This Video Works — full width */}
          <div className="md:col-span-2 space-y-2">
            <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Why This Video Works</label>
            <textarea
              value={formData.why_it_works}
              onChange={e => setFormData({...formData, why_it_works: e.target.value})}
              className={`${inputClasses} h-24 resize-none`}
              placeholder="Explain why this video is effective..."
            />
          </div>

          {/* Admin Controls */}
          {role === 'admin' && (
            <div className="md:col-span-2 p-6 bg-orange-500/5 border border-orange-500/10 rounded-3xl space-y-6 mt-4">
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={FiTrendingUp} className="text-orange-500" />
                <h4 className="text-sm font-bold text-orange-400 uppercase tracking-widest">Admin Controls</h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Impact Score</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={formData.market_score}
                    onChange={e => setFormData({...formData, market_score: e.target.value})}
                    className="w-full pl-4 pr-4 py-3 bg-zinc-900 border border-orange-500/20 rounded-2xl text-white focus:border-orange-500 transition-all outline-none font-mono text-center"
                    placeholder="0.0"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-slate-400 text-xs font-bold uppercase tracking-widest">Total Views</label>
                  <div className="relative">
                    <SafeIcon icon={FiEye} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                    <input
                      type="number"
                      min="0"
                      value={formData.views}
                      onChange={e => setFormData({...formData, views: e.target.value})}
                      className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-white/10 rounded-2xl text-white focus:border-orange-500 transition-all outline-none font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-4 pt-8 border-t border-white/5">
          <button type="button" onClick={onClose} className="btn-secondary">Discard</button>
          <button type="submit" className="btn-primary flex items-center gap-2 px-8">
            <SafeIcon icon={FiSave} />
            {video ? 'Save Changes' : 'Publish Now'}
          </button>
        </div>
      </form>

      {role === 'admin' && (
        <>
          {editors.ind && <DropdownEditor title="Industries" items={industries} onSave={saveIndustries} onClose={() => setEditors({...editors, ind: false})} />}
          {editors.sty && <DropdownEditor title="Visual Styles" items={visualStyles} onSave={saveVisualStyles} onClose={() => setEditors({...editors, sty: false})} />}
          {editors.typ && <DropdownEditor title="Video Types" items={videoTypes} onSave={saveVideoTypes} onClose={() => setEditors({...editors, typ: false})} />}
        </>
      )}
    </motion.div>
  );
};

export default VideoForm;